import { Component, EventEmitter, Inject, Input, OnChanges, OnInit, Optional, Output, SimpleChanges, TemplateRef, ViewChild } from '@angular/core';
import { BaseComponent } from 'src/app/core/base.component';
import { SessionService } from 'src/app/shared/services/session.service';
import { StringHelpers } from 'src/app/shared/helpers/string-helpers';
import { LeagueIterationMemberService } from '../services/league-iteration-member.service';
import { LeagueIteration } from '../models/league-iteration';
import { LeagueIterationMember } from '../models/league-iteration-member';
import { MatDialog } from '@angular/material/dialog';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { provideNativeDateAdapter } from '@angular/material/core';
import { AlertModalComponent } from 'src/app/shared/modals/alert-modal/alert-modal.component';
import { AddMembersToIterationModalComponent } from '../modals/league-member/add-members-to-iteration-modal/add-members-to-iteration-modal.component';
import { LeagueIterationMemberList } from '../models/league-iteration-member-list';
import { environment } from 'src/environments/environment';
import { PaymentType } from 'src/app/shared/models/payment-type-enum';

@Component({
    selector: 'league-iteration-members-list',
    templateUrl: './league-iteration-members-list.component.html',
    styleUrl: './league-iteration-members-list.component.scss',
    providers: [provideNativeDateAdapter()],
    standalone: false
})
export class LeagueIterationMembersListComponent extends BaseComponent implements OnInit, OnChanges {
    @Input() iteration: LeagueIteration | null = null;
    @Output() membersUpdated = new EventEmitter<void>();

    public iterationMembers: LeagueIterationMemberList[] = [];
    public displayedColumns: string[] = ['memberName', 'email', 'paid', 'paymentType', 'actions'];
    private paymentLinkMembers: any;
    
    public paymentTypes = [
        { value: PaymentType.CashCheck, label: 'Cash/Check' },
        { value: PaymentType.Card, label: 'Card' },
        { value: PaymentType.Venmo, label: 'Venmo' },
        { value: PaymentType.Other, label: 'Other' }
    ];
    public allMembersPaid: boolean = false;

    constructor(
        @Optional() @Inject(MAT_DIALOG_DATA) public data: LeagueIteration | null,
        @Optional() public dialogRef: MatDialogRef<LeagueIterationMembersListComponent>,
        sessionService: SessionService,
        stringHelpers: StringHelpers,
        private dialog: MatDialog,
        private leagueIterationMemberService: LeagueIterationMemberService
    ) {
        super(sessionService, stringHelpers);

        if (this.data && !this.iteration) {
            this.iteration = this.data;
        }
        this.isDarkMode = !this.sessionService.getUser().defaultTheme;
    }

    ngOnInit(): void {
        if (this.iteration) {
            this.getIterationMembers();
            this.paymentLinkMembers = {
                leagueId: this.iteration?.leagueId,
                leagueIterationYear: this.iteration?.year,
                organizationId: this.sessionService.getUser().organizationId,
                stripeAccountId: this.sessionService.getUser().stripeAccountId,
                stripePriceId: this.iteration?.stripePriceId,
                successUrl: `${environment.dashboardLocation}/payment-success-failure`,
                leagueIterationPaymentLinkMembers: []
            };
        }
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['iteration'] && this.iteration && this.iteration.id) {
            const previousId = changes['iteration'].previousValue?.id;
            const currentId = this.iteration.id;

            if (!previousId || previousId !== currentId) {
                this.getIterationMembers();
            }
        }
    }

    getIterationMembers(): void {
        if (!this.iteration || !this.iteration.id) {
            return;
        }

        this.isLoading = true;
        this.leagueIterationMemberService.getAllByIterationId({
            leagueIterationId: this.iteration.id,
            organizationId: this.sessionService.getUser().organizationId,
            pageSize: 1000,
            pageNumber: 1
        }).subscribe({
            next: (result: any) => {
                this.iterationMembers = result.results || [];
                this.isLoading = false;
                this.allMembersPaid = this.iterationMembers.every(member => member.paid);
            },
            error: (error: any) => {
                this.isLoading = false;
                console.error(error);
                this.alert(error);
            }
        });
    }

    onAddMembers(): void {
        if (!this.iteration.leagueId) {
            return;
        }

        const dialogRef = this.dialog.open(AddMembersToIterationModalComponent, {
            width: '700px',
            maxWidth: '90vw',
            data: {
                league: { id: this.iteration.leagueId },
                iteration: this.iteration
            }
        });

        dialogRef.afterClosed().subscribe((result) => {
            if (result === 'save') {
                this.getIterationMembers();
                this.notifyParent();
            }
        });
    }

    notifyParent(): void {
        this.membersUpdated.emit();
    }

    onTogglePayment(leagueIterationMember: LeagueIterationMember): void {
        this.isLoading = true;
        const data = {
            id: leagueIterationMember.id,
            paid: !leagueIterationMember.paid,
            leagueIterationId: this.iteration?.id,
            leagueMemberId: leagueIterationMember.leagueMemberId,
            organizationId: this.sessionService.getUser().organizationId,
            lastEditedBy: this.sessionService.getUserId()
        };

        this.leagueIterationMemberService.update(leagueIterationMember.id, data).subscribe({
            next: (result) => {
                this.getIterationMembers();
                this.isLoading = false;
                this.notifyParent();
            },
            error: (error) => {
                this.alert(error);
                console.error(error);
                this.isLoading = false;
            }
        });
    }

    onRemoveMember(member: LeagueIterationMemberList): void {
        const dialogRef = this.dialog.open(AlertModalComponent, {
            width: '35vw',
        });

        dialogRef.componentInstance.title = 'Remove Member from Iteration';
        dialogRef.componentInstance.body = `<h3 class="text-danger mat-h3">WARNING! THIS ACTION CANNOT BE UNDONE</h3> <br /> 
        Are you sure you want to remove <strong>${member.firstName} ${member.lastName}</strong> from iteration <strong>${this.iteration?.year}</strong>? 
        <br /><br />This will remove their registration and payment record for this iteration.`;

        dialogRef.componentInstance.submitText = 'Yes';
        dialogRef.componentInstance.otherText = 'No';
        dialogRef.componentInstance.isOtherTextDisabled = false;

        dialogRef.afterClosed().subscribe((response) => {
            if (response === 'Yes') {
                this.isLoading = true;
                this.leagueIterationMemberService.delete({
                    id: member.id,
                    lastEditedBy: this.sessionService.getUserId(),
                    organizationId: this.sessionService.getUser().organizationId,
                }).subscribe({
                    next: (result) => {
                        this.getIterationMembers();
                        this.isLoading = false;
                        this.notifyParent();
                    },
                    error: (error) => {
                        this.alert(error);
                        console.error(error);
                        this.isLoading = false;
                    }
                });
            }
        });
    }

    onSendPaymentLink(leagueIterationMember: LeagueIterationMemberList): void {
        this.paymentLinkMembers.leagueIterationPaymentLinkMembers.push({
            emailAddress: leagueIterationMember.email,
            leagueIterationMemberId: leagueIterationMember.id,
            leagueIterationMembername: this.getMemberName(leagueIterationMember),
            stripeCustomerId: leagueIterationMember.stripeCustomerId
        });
        if (this.paymentLinkMembers.leagueIterationPaymentLinkMembers.length > 0) {
            this.sendPaymentLink();
        }
    }

    onSendAllPaymentLinks(): void {
        const dialogRef = this.dialog.open(AlertModalComponent, {
            width: '35vw',
        });

        dialogRef.componentInstance.title = 'Send Payment Links';
        dialogRef.componentInstance.body = 'Are you sure you want to send payment links to all unpaid members? This will void any existing payment links for this iteration.';
        dialogRef.componentInstance.submitText = 'Yes';
        dialogRef.componentInstance.otherText = 'No';
        dialogRef.componentInstance.isOtherTextDisabled = false;

        dialogRef.afterClosed().subscribe((response) => {
            if (response === 'Yes') {
                this.iterationMembers.forEach(leagueIterationMember => {
                    if (!leagueIterationMember.paid) {
                        this.paymentLinkMembers.leagueIterationPaymentLinkMembers.push({
                            emailAddress: leagueIterationMember.email,
                            leagueIterationMemberId: leagueIterationMember.id,
                            leagueIterationMembername: this.getMemberName(leagueIterationMember),
                            stripeCustomerId: leagueIterationMember.stripeCustomerId
                        });
                    }
                });

                if (this.paymentLinkMembers.leagueIterationPaymentLinkMembers.length > 0) {
                    this.sendPaymentLink();
                }
            }
        });
    }

    sendPaymentLink(): void {
        this.isLoading = true;
        this.leagueIterationMemberService.createCheckoutSession(this.paymentLinkMembers).subscribe({
            next: (result) => {
                const dialogRef = this.dialog.open(AlertModalComponent, {
                    width: '35vw',
                });
        
                dialogRef.componentInstance.title = 'Payment Link Sent';
                dialogRef.componentInstance.body = 'Payment link sent successfully';
                dialogRef.componentInstance.submitText = 'Close';
                dialogRef.componentInstance.isOtherTextDisabled = true;
                dialogRef.afterClosed().subscribe((response) => {
                    if (response === 'Close') {
                        this.getIterationMembers();
                        this.notifyParent();
                    }
                });
            },
            error: (error) => {
                this.isLoading = false;
                this.alert(error);
                console.error(error);
            }
        });
    }

    getMemberName(member: LeagueIterationMemberList): string {
        return `${member.firstName} ${member.lastName}`;
    }

    @ViewChild('myTemplate') myTemplate: TemplateRef<any>;
    setPaymentType(leagueIterationMember: LeagueIterationMemberList): void {
        const dialogRef = this.dialog.open(this.myTemplate, {
            width: '35vw',
            data: { ...leagueIterationMember }
        });
        dialogRef.afterClosed().subscribe((result) => {
            if (result && result.paymentType !== undefined) {
                const data = {
                    id: leagueIterationMember.id,
                    paymentType: result.paymentType,
                    paid: true,
                    leagueIterationId: this.iteration?.id,
                    leagueMemberId: leagueIterationMember.leagueMemberId,
                    organizationId: this.sessionService.getUser().organizationId,
                    lastEditedBy: this.sessionService.getUserId()
                };

                this.isLoading = true;
                this.leagueIterationMemberService.update(leagueIterationMember.id, data).subscribe({
                    next: (result) => {
                        this.getIterationMembers();
                        this.isLoading = false;
                        this.notifyParent();
                    },
                    error: (error) => {
                        this.alert(error);
                        console.error(error);
                        this.isLoading = false;
                    }
                });
            }
        });
    }

    getPaymentType(paymentType: PaymentType): string {
        switch (paymentType) {
            case PaymentType.CashCheck:
                return 'Cash/Check';
            case PaymentType.Card:
                return 'Card';
            case PaymentType.Venmo:
                return 'Venmo';
            case PaymentType.Other:
                return 'Other';
            default:
                return 'N/A';
        }
    }

    alert(error: any): void {
        const dialogRef = this.dialog.open(AlertModalComponent, {
            width: '35vw',
        });

        let errorMessage = '<h3 class="mat-h3">You have following errors:</h3><ul> ';

        if (error.status === 400) {
            if (Array.isArray(error.error)) {
                error.error.forEach((error: any) => {
                    errorMessage += `<li>${error.errorMessage}</li>`;
                });
            } else {
                errorMessage += `<li>${error.error}</li>`;
            }
        } else {
            errorMessage += `<li>There was an error loading the iteration members, Please try again or contact support if the problem persists</li>`;
        }

        dialogRef.componentInstance.title = 'Iteration Members Error';
        dialogRef.componentInstance.body = errorMessage + '</ul>';
        dialogRef.componentInstance.submitText = 'OK';
    }

    close(): void {
        this.dialogRef.close();
    }
}

